/**
 * AI enrichment for a timeline. Owner-agnostic: the caller supplies the
 * callable and the labels, so a brand and a manufacturer share one dialog
 * rather than two copies that drift.
 *
 * Presentation and fetching only — the Timeline section owns persistence, as it
 * already owns the one write path for `history`. The dialog hands back the
 * events the curator ticked plus the apply mode; putting the write here would
 * be a second writer for the same array.
 *
 * Not built on `BulkAIEnrich`: that is a refs × fields grid, and this is one
 * entity producing a variable-length array of events. What IS reused is the
 * machinery around it — the result cache, so a dismissed dialog does not cost a
 * second run; the unapplied-results guard, so closing asks first; and the
 * source-URL fetch.
 *
 * Every event is a specific factual claim, so nothing is applied without a tick.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import type {
  AIHistoryResult,
  AIProposedTimelineEvent,
} from '../../services/firebase';
import { fetchSourceUrlContent } from '../../services/sourceUrlContent';
import { useEnrichResultCache } from '../../hooks/useEnrichResultCache';
import {
  TIMELINE_CONFIDENCE_FILTER_LABELS,
  confidenceLabel,
  bandForScore,
  meetsTimelineConfidence,
  type TimelineConfidenceFilter,
} from '../../services/timelineConfidence';
import { parseTimelineYear } from '../../utils/timeline';

export type TimelineApplyMode = 'merge' | 'replace';

interface Props {
  isOpen: boolean;
  /** Namespaces the result cache, so brand runs never serve a manufacturer's. */
  ownerKind: 'brand' | 'manufacturer' | 'parentOrg';
  ownerId: string;
  ownerName: string;
  /** Runs the owner's enrichment callable. */
  enrich: (input: {
    additionalContext?: string[];
    sourceUrlContent?: string;
  }) => Promise<AIHistoryResult>;
  /** Existing events, for the duplicate check the merge mode reports. */
  existing: ReadonlyArray<{ date: string; title: string }>;
  onClose: () => void;
  /** Folds the events in AND persists them — the dialog is self-sufficient. */
  onApply: (events: AIProposedTimelineEvent[], mode: TimelineApplyMode) => Promise<void>;
}

/** Cached payload — the answer, not this session's review state. */
interface CachedHistory {
  events: AIProposedTimelineEvent[];
  dropped: number;
}

/** Why an event is not ticked. Mirrors the reference grid's skip reasons. */
type SkipReason = 'checked' | 'belowThreshold' | 'duplicate';

const SKIP_LABEL: Record<SkipReason, string> = {
  checked: 'Will be applied.',
  belowThreshold: 'Below the confidence filter. Tick it to apply anyway.',
  duplicate:
    'Already on this timeline. Tick it to add a second copy anyway.',
};

const dupKey = (date: string, title: string) =>
  `${date.trim().toLowerCase()}|${title.trim().toLowerCase()}`;

export function TimelineEnrichDialog({
  isOpen,
  ownerKind,
  ownerId,
  ownerName,
  enrich,
  existing,
  onClose,
  onApply,
}: Props) {
  // Namespaced by owner kind: `buildEnrichCacheKey` already separates on kind,
  // so a brand and a manufacturer sharing an id cannot collide.
  const cache = useEnrichResultCache<CachedHistory>(`${ownerKind}History`);

  const [contextBlocks, setContextBlocks] = useState<string[]>(['']);
  const [sourceUrl, setSourceUrl] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CachedHistory | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [filter, setFilter] = useState<TimelineConfidenceFilter>('highMedium');
  const [mode, setMode] = useState<TimelineApplyMode>('merge');
  const [overrides, setOverrides] = useState<Record<number, boolean>>({});
  const [applied, setApplied] = useState(false);
  const [pendingExit, setPendingExit] = useState<{ proceed: () => void } | null>(null);

  // Reset per opening — a previous manufacturer's answer must not appear here.
  useEffect(() => {
    if (!isOpen) return;
    setResult(null);
    setFromCache(false);
    setOverrides({});
    setApplied(false);
  }, [isOpen, ownerId]);

  /**
   * What the answer depends on, cheap to compute.
   *
   * Keyed on the source URL rather than its fetched text: fetching is the work
   * a cache hit exists to skip, and the five-minute TTL bounds how stale a page
   * can be. Context blocks hash as an array, so adding, editing or reordering
   * one misses naturally.
   */
  const cacheInputs = useMemo(
    () => ({
      ownerId,
      contextBlocks: contextBlocks.map((b) => b.trim()).filter(Boolean),
      sourceUrl: sourceUrl.trim(),
    }),
    [ownerId, contextBlocks, sourceUrl],
  );

  const existingKeys = useMemo(
    () => new Set(existing.map((e) => dupKey(e.date, e.title))),
    [existing],
  );

  /**
   * Per-event default tick state, the reason, and whether the event is already
   * on the timeline.
   *
   * `onTimeline` is computed WITHOUT reference to the apply mode, so flipping
   * to Replace does not make an applied event look un-applied — the badge is a
   * statement about the document, not about what this click would do. The mode
   * only decides whether it starts ticked: under Merge a duplicate is skipped,
   * under Replace it must be re-included or Replace would delete it.
   */
  const decisions = useMemo(() => {
    const events = result?.events ?? [];
    return events.map(
      (e): { checked: boolean; reason: SkipReason; onTimeline: boolean } => {
        const onTimeline = existingKeys.has(dupKey(e.date, e.title));
        if (!meetsTimelineConfidence(e.aiConfidence, filter)) {
          return { checked: false, reason: 'belowThreshold', onTimeline };
        }
        if (mode === 'merge' && onTimeline) {
          return { checked: false, reason: 'duplicate', onTimeline };
        }
        return { checked: true, reason: 'checked', onTimeline };
      },
    );
  }, [result, filter, mode, existingKeys]);

  const isChecked = useCallback(
    (i: number) => overrides[i] ?? decisions[i]?.checked ?? false,
    [overrides, decisions],
  );

  const checkedEvents = useMemo(
    () => (result?.events ?? []).filter((_, i) => isChecked(i)),
    [result, isChecked],
  );
  const checkedEventsCount = checkedEvents.length;

  // Only unsaved proposals are worth warning about: applying now writes
  // through, so anything ticked-and-applied is already on the document.
  const hasUnapplied = checkedEventsCount > 0 && !applied;

  const requestClose = () => {
    if (!hasUnapplied) {
      onClose();
      return;
    }
    setPendingExit({ proceed: onClose });
  };

  const run = async (bypassCache: boolean) => {
    if (running) return;
    setRunning(true);
    try {
      if (bypassCache) cache.invalidate(ownerId, cacheInputs);
      else {
        const hit = cache.getCached(ownerId, cacheInputs);
        if (hit) {
          setResult(hit.value);
          setFromCache(true);
          setOverrides({});
          setApplied(false);
          toast.success('Reused a cached result — no AI credits spent.');
          return;
        }
      }

      const scraped = sourceUrl.trim()
        ? await fetchSourceUrlContent(sourceUrl.trim())
        : undefined;

      const res = await enrich({
        additionalContext: cacheInputs.contextBlocks,
        ...(scraped ? { sourceUrlContent: scraped } : {}),
      });

      const value: CachedHistory = { events: res.events ?? [], dropped: res.dropped ?? 0 };
      cache.putCached(ownerId, cacheInputs, value);
      setResult(value);
      setFromCache(false);
      setOverrides({});
      setApplied(false);
      toast.success(
        `${value.events.length} event${value.events.length === 1 ? '' : 's'} proposed.`,
      );
    } catch (err) {
      console.error('Timeline enrichment failed:', err);
      toast.error(err instanceof Error ? err.message : 'Enrichment failed.');
    } finally {
      setRunning(false);
    }
  };

  const [applying, setApplying] = useState(false);
  const apply = async () => {
    if (checkedEvents.length === 0 || applying) return;
    setApplying(true);
    try {
      await onApply(checkedEvents, mode);
      setApplied(true);
      setOverrides({});
      // Close on success. The section behind this dialog now shows the saved
      // rows, and there is no longer a Save button to hunt for — the merge is
      // already on the document.
      onClose();
    } catch (err) {
      // The write failed — keep the review on screen so the run is not lost.
      console.error('Applying timeline enrichment failed:', err);
      toast.error('Could not save those events. Nothing was applied.');
    } finally {
      setApplying(false);
    }
  };

  const tally = useMemo(() => {
    const t = { checked: 0, belowThreshold: 0, duplicate: 0 };
    decisions.forEach((d, i) => {
      if (isChecked(i)) t.checked += 1;
      else if (d.reason !== 'checked') t[d.reason] += 1;
    });
    return t;
  }, [decisions, isChecked]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        title={`AI timeline — ${ownerName}`}
        size="full"
        dismissOnBackdrop={false}
      >
        <div className="space-y-4">
          {/* ── inputs ─────────────────────────────────────────────────── */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Research context
              </span>
              <button
                type="button"
                className="text-[11px] text-indigo-600 hover:underline"
                disabled={running}
                onClick={() => setContextBlocks((b) => [...b, ''])}
              >
                + Add another source
              </button>
            </div>
            <p className="text-[11px] text-gray-500">
              Paste research — one block per source. Treated as ground truth the model is told
              not to contradict, which is the main lever against invented events.
            </p>
            {contextBlocks.map((block, i) => (
              <div key={i} className="flex gap-2">
                <textarea
                  value={block}
                  disabled={running}
                  onChange={(e) =>
                    setContextBlocks((prev) => prev.map((b, j) => (j === i ? e.target.value : b)))
                  }
                  rows={3}
                  placeholder={`Source ${i + 1} — Wikipedia, brand site, auction lot, museum archive…`}
                  className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                />
                {contextBlocks.length > 1 && (
                  <button
                    type="button"
                    className="text-[11px] text-red-600 hover:underline self-start"
                    disabled={running}
                    onClick={() => setContextBlocks((prev) => prev.filter((_, j) => j !== i))}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Source URL (optional)
              </span>
              <input
                type="url"
                value={sourceUrl}
                disabled={running}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://…  — fetched and sent as further context"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <Button onClick={() => void run(false)} loading={running} disabled={running}>
              {result ? 'Run again' : 'Run enrichment'}
            </Button>
            {result && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void run(true)}
                disabled={running}
                title="Ignore the cached result and spend a fresh AI call."
              >
                ↻ Retry, ignoring cache
              </Button>
            )}
            {fromCache && (
              <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-sky-800">
                cached
              </span>
            )}
          </div>

          {/* ── results ────────────────────────────────────────────────── */}
          {result && (
            <div className="space-y-3 border-t pt-3">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs text-gray-700">
                  Confidence:{' '}
                  <select
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value as TimelineConfidenceFilter);
                      setOverrides({});
                    }}
                    className="border border-gray-300 rounded px-1 py-0.5 text-xs bg-white"
                  >
                    {(
                      Object.keys(TIMELINE_CONFIDENCE_FILTER_LABELS) as TimelineConfidenceFilter[]
                    ).map((f) => (
                      <option key={f} value={f}>
                        {TIMELINE_CONFIDENCE_FILTER_LABELS[f]}
                      </option>
                    ))}
                  </select>
                </label>

                <fieldset className="flex items-center gap-2 text-xs text-gray-700">
                  <legend className="sr-only">Apply mode</legend>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="timelineApplyMode"
                      checked={mode === 'merge'}
                      onChange={() => {
                        setMode('merge');
                        setOverrides({});
                      }}
                    />
                    Merge <span className="text-gray-400">(keeps existing)</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="timelineApplyMode"
                      checked={mode === 'replace'}
                      onChange={() => {
                        setMode('replace');
                        setOverrides({});
                      }}
                    />
                    <span className="text-red-700">Replace</span>
                  </label>
                </fieldset>
              </div>

              {mode === 'replace' && (
                <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  Replace discards all {existing.length} existing event
                  {existing.length === 1 ? '' : 's'}, including any you wrote by hand and any
                  images attached to them. Merge is the safe option.
                </p>
              )}

              <p className="text-[11px] text-gray-500">
                {tally.checked} of {result.events.length} ticked
                {tally.belowThreshold > 0 && ` · ${tally.belowThreshold} below the filter`}
                {tally.duplicate > 0 && ` · ${tally.duplicate} already on the timeline`}
                {result.dropped > 0 && ` · ${result.dropped} unusable and discarded`}.
              </p>

              <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                {result.events.map((event, i) => {
                  const decision = decisions[i];
                  const band = bandForScore(event.aiConfidence);
                  return (
                    <label
                      key={i}
                      className={`flex gap-2 rounded border p-2 cursor-pointer ${
                        decision.onTimeline && mode === 'merge'
                          ? 'border-green-200 bg-green-50/40'
                          : isChecked(i)
                            ? 'border-gray-200 bg-white'
                            : 'border-gray-100 bg-gray-50'
                      }`}
                      title={SKIP_LABEL[isChecked(i) ? 'checked' : decision.reason]}
                    >
                      {decision.onTimeline && mode === 'merge' ? (
                        // Already saved. A bare empty checkbox read as "not
                        // selected" when it actually meant "already done" —
                        // the counter said 11 applied while every row looked
                        // untouched. Shown rather than hidden, so the run stays
                        // auditable, and disabled so it cannot be re-merged.
                        <span
                          className="mt-0.5 shrink-0 text-green-600"
                          title="Already on this timeline."
                          aria-label="Already on the timeline"
                        >
                          ✓
                        </span>
                      ) : (
                        <input
                          type="checkbox"
                          className="mt-1 shrink-0"
                          checked={isChecked(i)}
                          disabled={applying}
                          onChange={(e) =>
                            setOverrides((prev) => ({ ...prev, [i]: e.target.checked }))
                          }
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="font-mono text-xs text-gray-500">{event.date}</span>
                          <span className="text-sm font-medium text-gray-900">{event.title}</span>
                          {decision.onTimeline && (
                            <span className="rounded-full bg-green-100 px-1.5 text-[10px] font-medium uppercase tracking-wide text-green-800">
                              on timeline
                            </span>
                          )}
                          {event.category && (
                            <span className="rounded bg-gray-100 px-1.5 text-[10px] uppercase tracking-wide text-gray-600">
                              {event.category}
                            </span>
                          )}
                          <span
                            className={`text-[10px] ${
                              band === 'high'
                                ? 'text-green-700'
                                : band === 'medium'
                                  ? 'text-amber-700'
                                  : 'text-gray-400'
                            }`}
                          >
                            {confidenceLabel(event.aiConfidence)}
                          </span>
                          {parseTimelineYear(event.date) === Infinity && (
                            <span className="text-[10px] text-amber-700">no readable year</span>
                          )}
                        </div>
                        <div
                          className="mt-0.5 text-xs text-gray-600 line-clamp-3"
                          // The callable returns rich-text HTML for this field.
                          dangerouslySetInnerHTML={{ __html: event.description }}
                        />
                        {event.sourceUrl && (
                          <a
                            href={event.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="mt-0.5 block truncate text-[10px] text-indigo-600 hover:underline"
                          >
                            {event.sourceUrl}
                          </a>
                        )}
                        {event.image?.aiSuggestion && (
                          <p className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[10px] text-amber-800">
                            <span className="font-medium">Needs an image:</span>{' '}
                            {event.image.aiSuggestion}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex items-center gap-2 border-t pt-3">
                <Button
                  onClick={() => void apply()}
                  loading={applying}
                  disabled={checkedEvents.length === 0 || running || applying}
                >
                  {mode === 'replace' ? 'Replace &' : 'Merge &'} Save{' '}
                  {checkedEvents.length} event{checkedEvents.length === 1 ? '' : 's'}
                </Button>
                <span className="text-[11px] text-gray-400">
                  Writes straight through and closes — no separate Save
                  needed.
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={pendingExit !== null}
        onClose={() => setPendingExit(null)}
        title="Unapplied enrichment results"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            You have proposed events that haven’t been applied — close anyway?
          </p>
          <p className="text-xs text-gray-500">
            The result is cached for five minutes, so reopening and running again within that
            window costs no AI credits.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setPendingExit(null)} autoFocus>
              Keep open
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                const go = pendingExit?.proceed;
                setPendingExit(null);
                go?.();
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
